import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";
import { Container, Point } from "pixi.js";

import { IReelData } from "../../../abstracts/elements/AbstractReel";
import { EventsManager } from "../../../abstracts/events/EventsManager";
import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { IScatterDecoratorData } from "../../../abstracts/types/interfaces";
import { flat, isAllTrue, shuffleArray } from "../../../abstracts/utils/ArrayUtils";
import { fitDisplayObjectToWidth } from "../../../abstracts/utils/GraphicUtils";
import { addSkip, getRandomElement } from "../../../abstracts/utils/JSUtils";
import { fadeAndStopSound, playSound } from "../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../Apocalypse";
import {
    ApocalypseSoundNames,
    ApocalypseSpineNames,
    getNextM4FeatureSound,
    m1FeatureSounds,
    m2FeatureSounds,
    m3FeatureSounds,
    m4UpgradeSounds,
    m5FeatureSounds,
} from "../../assetsLoader/ApocalypseAssetsNames";
import {
    fastSpinDelay,
    fsConfig,
    getWinLevel,
    m4HighlightDelay,
    m5HighlightDelay,
    reelsConfig,
    symbolConfig,
    winLevels,
} from "../../config/ApocalypseConfig";
import { SHOW_EASTER_EGG_ANIMATION } from "../../events/ApocalypseEvents";
import { ApocalypseFsContainer } from "../../modules/Reels/fs/ApocalypseFsContainer";
import { ApocalypseFsReel } from "../../modules/Reels/fs/ApocalypseFsReel";
import ApocalypseReel from "../../modules/Reels/normal/ApocalypseReel";
import {
    getBitmapText,
    returnBitmapText,
} from "../../modules/Reels/symbols/ApocalypseBitmapTextPool";
import { ApocalypseBlankSymbol } from "../../modules/Reels/symbols/ApocalypseBlankSymbol";
import { ApocalypseSymbol } from "../../modules/Reels/symbols/ApocalypseSymbol";
import {
    getSymbolFromPool,
    returnSymbolToPool,
} from "../../modules/Reels/symbols/ApocalypseSymbolsPool";
import {
    ApocalypseHighSymbolNames,
    ApocalypseSpecialSymbolNames,
} from "../../types/ApocalypseEnums";
import { ApocalypseGameFeatureObj } from "../../types/ApocalypseInterfaces";
import { cloneSymbolValue, shake } from "../../utils/ApocalypseGraphicUtils";
import { fsMatrix } from "../../utils/ApocalypseJSUtils";
import { addExtraSpin } from "./ApocalypseFsFirstStageDecorator";

const getFlatIndex = (reelIndex: number, rowIndex: number) => {
    return reelIndex * reelsConfig.reelsNumber + rowIndex;
};

export const ApocalypseFsSecondStageDecorator = (data: IScatterDecoratorData): void => {
    const {
        finalDataReels = [],
        gameReels = [],
        timeline = gsap.timeline(),
        stopSpinDelay = 0,
        // wasFeatureBuy = false,
        onDecoratorComplete = () => {},
        reelsContainer = new Container(),
    } = data;

    const fsContainer = reelsContainer as ApocalypseFsContainer;
    const flatEvaluatedData = flat(Object.assign([], finalDataReels));
    const stoppedReels: boolean[] = new Array(finalDataReels.length).fill(false);
    const reelsLanded: boolean[] = new Array(finalDataReels.length).fill(false);
    const reversedPersistanceMap = fsMatrix(Apocalypse.model.persistenceMap);
    const flatReversedPersistanceMap = flat(reversedPersistanceMap);
    const orderedFeaturesIndexes: number[] = Apocalypse.model.featuresMap
        .map((feature) => {
            return getFlatIndex(feature.symbolCoord.reel, feature.symbolCoord.row);
        })
        .sort((a, b) => {
            return a - b;
        });
    let totalWinValue = fsContainer.fsMultiplierContainer.counter.valueObj.value;

    let nextFeatureIndex = 0;

    shuffleArray(fsContainer.lowLockerSoundNames);
    fsContainer.lowLockerCurrentSoundIndex = 0;

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_START, `>`);

    const allReelsFilled = (): boolean => {
        let filledCount = 0;

        gameReels.forEach((reel, reelIndex) => {
            if (
                reelIndex > 5 &&
                reelIndex < 30 &&
                (reel as ApocalypseFsReel).getSymbolAtIndex(1).name !==
                    ApocalypseSpecialSymbolNames.DUMP &&
                (reel as ApocalypseFsReel).getSymbolAtIndex(1).name !==
                    ApocalypseSpecialSymbolNames.BLANK
            ) {
                filledCount++;
            }
        });

        if (filledCount === 24) {
            return true;
        }

        return false;
    };

    let alreadyDisabledM5 = false;
    const maxWinLevel = winLevels[9];

    const animateFeatures = () => {
        const finishDecorator = () => {
            gsap.timeline({
                onComplete: () => {
                    onDecoratorComplete();
                },
                delay: 0.2,
            });
        };

        if (Apocalypse.model.featuresMap.length === 0) {
            finishDecorator();
        } else {
            if (
                nextFeatureIndex === orderedFeaturesIndexes.length ||
                totalWinValue >= maxWinLevel.min
            ) {
                finishDecorator();
            } else {
                const getFeature = (): undefined | ApocalypseGameFeatureObj => {
                    let feature: undefined | ApocalypseGameFeatureObj = undefined;
                    Apocalypse.model.featuresMap.forEach((featureObj, featureIndex) => {
                        if (featureIndex === nextFeatureIndex) {
                            feature = featureObj;
                        }
                    });

                    return feature;
                };

                const feature = getFeature();

                if (feature !== undefined) {
                    const tl = gsap.timeline({
                        smoothChildTiming: true,
                        autoRemoveChildren: true,
                        onComplete: () => {
                            switch (feature.name) {
                                case `M4`:
                                    getM4FeatureAnimation(feature, animateFeatures);
                                    break;

                                case `M3`:
                                    getM3FeatureAnimation(feature, animateFeatures);
                                    break;

                                case `M2`:
                                    getM2FeatureAnimation(feature, animateFeatures);
                                    break;

                                case `M1`:
                                    getM1FeatureAnimation(feature, animateFeatures);
                                    break;

                                default:
                                    getM5FeatureAnimation(feature, animateFeatures);
                                    break;
                            }
                        },
                    });

                    const allReelsFilledBool = allReelsFilled();

                    const checkIfAllFilledAndDisableM5 = () => {
                        if (allReelsFilledBool && !alreadyDisabledM5) {
                            tl.add(fsContainer.hideAllM5FeatureSymbols());
                            alreadyDisabledM5 = true;
                        }
                    };

                    checkIfAllFilledAndDisableM5();
                }

                nextFeatureIndex++;
            }
        }
    };

    const onReelSpinComplete = (reelId: number): void => {
        const checkIfAllReelsStopped = () => {
            stoppedReels[reelId] = true;

            if (isAllTrue(stoppedReels)) {
                if (Apocalypse.model.lostRounds === 10) {
                    Apocalypse.model.lostRounds = 0;
                    EventsManager.trigger(SHOW_EASTER_EGG_ANIMATION);
                }

                animateFeatures();
            }
        };

        checkIfAllReelsStopped();
    };

    const onReelLand = (reelId: number): void => {
        // reelStopTimeline.resume();
        if (reelId <= reelsConfig.reelsNumber - 1 || reelId >= 30) {
            if (!gameReels[reelId].isLocked) {
                if (flatReversedPersistanceMap[reelId]) {
                    playSound({
                        name: ApocalypseSoundNames.M_PERSISTENT_SYMBOL_LAND,
                    });
                } else {
                    if (flatEvaluatedData[reelId] in ApocalypseHighSymbolNames) {
                        playSound({
                            name: ApocalypseSoundNames.M_SYMBOL_LAND,
                        });
                    }
                }
            }
        }

        reelsLanded[reelId] = true;
        if (isAllTrue(reelsLanded)) {
            fadeAndStopSound({
                name: ApocalypseSoundNames.REELS_SPIN,
                from: 1,
                to: 0,
                duration: 0.1,
            });
        }
    };

    const stopNextReels = () => {
        let reelStopTime = 0;

        let reelStopTimeline: gsap.core.Timeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
        });

        const getNextTeasingIndex = (): number => {
            const teasingIndexes: number[] = [];

            gameReels.forEach((gameReel, reelIndex) => {
                const reel = gameReel as ApocalypseFsReel;

                if (!reel.isLocked) {
                    teasingIndexes.push(reelIndex);
                }
            });

            return getRandomElement(teasingIndexes);
        };

        const teasingIndex = getNextTeasingIndex();

        gameReels.forEach((gameReel, reelIndex) => {
            const data: IReelData = {
                hasNearWin: false,
                stopSymbolNames: finalDataReels[reelIndex],
            };

            const reel = gameReel as ApocalypseFsReel;

            if (reel.isLocked) {
                // orice as face asta se triggaruie primul plm
                reelStopTimeline.addLabel(`reelLastStartStop_${reelIndex}`, 0);
                reelStopTimeline.add(
                    reel.stopNormalBetAnimation(data, onReelSpinComplete, onReelLand),
                    `reelLastStartStop_${reelIndex}`
                );
            } else {
                reelStopTime += stopSpinDelay;

                const currentTeaserRound = Apocalypse.model.currentSecondStageRound;
                const teaserRounds = Apocalypse.model.secondStageTeaserRounds;
                const indexOfTeaserRound = teaserRounds.indexOf(currentTeaserRound);

                const teasingCondition = reelIndex === teasingIndex && indexOfTeaserRound !== -1;

                reelStopTimeline.addLabel(`reelStartStop_${reelIndex}`, reelStopTime);
                reelStopTimeline.add(
                    reel.stopNormalBetAnimation(
                        data,
                        onReelSpinComplete,
                        onReelLand,
                        teasingCondition
                    ),
                    `reelStartStop_${reelIndex}`
                );

                if (teasingCondition) {
                    reelStopTime += fsConfig.reelTeasingDuration! + stopSpinDelay * 3;

                    if (flatReversedPersistanceMap[reelIndex]) {
                        reelStopTimeline.call(
                            () => {
                                const highLockAnimation =
                                    fsContainer.getLockHighReelAnimation(reelIndex);

                                if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                                    highLockAnimation.timeScale(2);
                                }
                            },
                            undefined,
                            `>+=${fsConfig.reelTeasingDuration! + 0.02}`
                        );

                        reelStopTime += 0.6;

                        reelStopTimeline.addLabel(`lockerEnd`, reelStopTime);
                    }
                } else {
                    if (flatReversedPersistanceMap[reelIndex]) {
                        const highLockAnimation = fsContainer.getLockHighReelAnimation(reelIndex);

                        if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                            highLockAnimation.timeScale(2);
                        }

                        reelStopTimeline.add(highLockAnimation, `>`);

                        reelStopTime += highLockAnimation.totalDuration();
                    }
                }
            }

            reelStopTimeline.addLabel(`reelStop_${reelIndex}`, `>`);
        });
    };

    stopNextReels();

    function getM5FeatureAnimation(
        feature: ApocalypseGameFeatureObj,
        onComplete: Function
    ): gsap.core.Timeline {
        let allReelsFilledBool = allReelsFilled();

        if (allReelsFilledBool) {
            return gsap.timeline({
                autoRemoveChildren: true,
                smoothChildTiming: true,
                onComplete: () => {
                    onComplete();
                },
            });
        }

        const m5ReelIndex = getFlatIndex(feature.symbolCoord.reel, feature.symbolCoord.row);

        /*
        ! handle reveal here maybe
        ;*/

        const locker = fsContainer.getLockerByReelId(m5ReelIndex);

        const m5FeatureTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    gsap.delayedCall(m5HighlightDelay, () =>
                        playSound({ name: getRandomElement(m5FeatureSounds) })
                    );
                } else {
                    playSound({ name: getRandomElement(m5FeatureSounds) });
                }
            },
            onComplete: () => {
                onComplete();

                allReelsFilledBool = allReelsFilled();

                if (!allReelsFilledBool) {
                    locker?.reset();
                }
            },
        });

        const m5Symbol = (
            gameReels[m5ReelIndex] as ApocalypseReel
        ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;

        const m5SymbolClone = getSymbolFromPool(m5Symbol.name) as ApocalypseSymbol;
        m5SymbolClone.alpha = 0;

        const globalPosition = m5Symbol.getGlobalPosition();
        const localPosition = fsContainer.toLocal(globalPosition);
        m5SymbolClone.x = localPosition.x;
        m5SymbolClone.y = localPosition.y;

        m5FeatureTimeline.add(
            gsap.to(m5SymbolClone, {
                alpha: 1,
                duration: 0.1,
                onStart: () => {
                    fsContainer.addChild(m5SymbolClone);

                    if (locker !== undefined) {
                        fsContainer.addChild(locker);
                    }
                },
            })
        );
        m5FeatureTimeline.add(m5SymbolClone.getHighSymbolFeatureAnimation());
        m5FeatureTimeline.call(
            () => {
                gsap.to(m5SymbolClone, {
                    alpha: 0,
                    duration: 0.2,
                    onComplete: () => {
                        returnSymbolToPool(m5SymbolClone);
                    },
                });
            },
            undefined,
            `>-=0.1`
        );

        feature.targetCoord.forEach((coordinates) => {
            if (coordinates.reel !== undefined && coordinates.row !== undefined) {
                const targetReelIndex = getFlatIndex(coordinates.reel, coordinates.row);

                const targetSymbol = (
                    gameReels[targetReelIndex] as ApocalypseReel
                ).getAllVisibleStopSymbols()[0] as ApocalypseBlankSymbol;

                m5FeatureTimeline.add(targetSymbol.getFeatureAnimation(5), 0.73);

                let symbolName = flatEvaluatedData[targetReelIndex];

                let m5ServerObj = Apocalypse.model.m5RevealMap.find((featureObj) => {
                    return (
                        m5ReelIndex ===
                        getFlatIndex(featureObj.m5Coord.reel, featureObj.m5Coord.row)
                    );
                });

                if (m5ServerObj !== undefined) {
                    symbolName = m5ServerObj.targetCoord.symbolName;
                }

                const newSymbol = getSymbolFromPool(symbolName) as ApocalypseSymbol;

                m5FeatureTimeline.add(
                    (gameReels[targetReelIndex] as ApocalypseFsReel).getSwitchSymbolsAnimation(
                        targetSymbol,
                        newSymbol
                    ),
                    `>-=0.4`
                );

                const globalPosition = targetSymbol.getGlobalPosition();
                const localPosition = fsContainer.toLocal(globalPosition);
                targetSymbol.x = localPosition.x;
                targetSymbol.y = localPosition.y;

                fsContainer.addChild(targetSymbol);

                m5FeatureTimeline.add(
                    fsContainer.getLockLowReelAnimation(targetReelIndex, undefined, false, true),
                    `>`
                );

                totalWinValue += newSymbol.valueObj.value;

                m5FeatureTimeline.add(
                    fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                        globalPosition,
                        newSymbol.valueObj.value,
                        totalWinValue
                    ),
                    `>`
                );

                /////////////////////////
                const newSymbolReelIndex = feature.targetCoord[0].row;

                let currReelCounter = -1;
                let isReelFilled = false;
                let reelFilledCounter = 0;

                gameReels.forEach((gameReel, reelIndex) => {
                    currReelCounter++;
                    if (currReelCounter === 6) {
                        currReelCounter = 0;
                    }
                    if (
                        reelIndex > 5 &&
                        reelIndex < 30 &&
                        currReelCounter === newSymbolReelIndex &&
                        (gameReel as ApocalypseFsReel).getSymbolAtIndex(1).name !==
                            ApocalypseSpecialSymbolNames.DUMP &&
                        (gameReel as ApocalypseFsReel).getSymbolAtIndex(1).name !==
                            ApocalypseSpecialSymbolNames.BLANK
                    ) {
                        reelFilledCounter++;
                        isReelFilled = reelFilledCounter === 4;
                    }
                });

                if (isReelFilled) {
                    const extraSpinTl = addExtraSpin(
                        fsContainer,
                        newSymbolReelIndex,
                        fsContainer.fsLeftContainer.counter.valueObj.value + 1
                    );

                    m5FeatureTimeline.add(extraSpinTl, `>`);
                    // m5FeatureTimeline.call(
                    //     () => {
                    //         SlotGame.keypadPlugin.setZeroBetSpinCounter(
                    //             fsContainer.fsLeftContainer.counter.valueObj.value
                    //         );
                    //     },
                    //     undefined,
                    //     `>-=0.25`
                    // );
                }
                /////////////////////////
            }
        });

        if (locker !== undefined) {
            m5FeatureTimeline.add(locker.getSpineTween(`idle_blinking`, true), 0);
        }

        addSkip(m5FeatureTimeline, data.flowStateController!);

        return m5FeatureTimeline;
    }

    function getM4FeatureAnimation(
        feature: ApocalypseGameFeatureObj,
        onComplete: Function
    ): gsap.core.Timeline {
        const m4ReelIndex = getFlatIndex(feature.symbolCoord.reel, feature.symbolCoord.row);

        const locker = fsContainer.getLockerByReelId(m4ReelIndex);

        const m4Symbol = (
            gameReels[m4ReelIndex] as ApocalypseReel
        ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;

        const m4SymbolClone = getSymbolFromPool(m4Symbol.name) as ApocalypseSymbol;
        m4SymbolClone.alpha = 0;

        let globalPosition = m4Symbol.getGlobalPosition();
        let localPosition = fsContainer.toLocal(globalPosition);
        m4SymbolClone.x = localPosition.x;
        m4SymbolClone.y = localPosition.y;

        const m4FeatureTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                fsContainer.addChild(m4SymbolClone);

                if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    gsap.delayedCall(m4HighlightDelay, () =>
                        playSound({ name: getNextM4FeatureSound() })
                    );
                } else {
                    playSound({ name: getNextM4FeatureSound() });
                }

                if (locker !== undefined) {
                    fsContainer.addChild(locker);
                }
            },
            onComplete: () => {
                onComplete();

                locker?.reset();
            },
        });

        m4FeatureTimeline.add(gsap.to(m4SymbolClone, { alpha: 1, duration: 0.1 }));
        m4FeatureTimeline.add(m4SymbolClone.getHighSymbolFeatureAnimation());

        const symbolAnimationTimings: number[] = [];
        const multiplierAnimationTimings: number[] = [];
        const mainTargetTl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
        });
        let currentTlPosition = 0;

        feature.targetCoord.forEach((coordinates, coordIndex) => {
            const targetReelIndex = getFlatIndex(coordinates.reel, coordinates.row);
            const targetReel = gameReels[targetReelIndex] as ApocalypseReel;
            const targetSymbol = targetReel.getAllVisibleStopSymbols()[0] as ApocalypseSymbol;
            const targetTl = gsap.timeline({
                smoothChildTiming: true,
                autoRemoveChildren: true,
                onComplete: () => {
                    returnSymbolToPool(featureAnimationSymbol);
                },
            });
            const symbolAnimationTl = gsap.timeline({
                smoothChildTiming: true,
                autoRemoveChildren: true,
                onStart: () => {
                    if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                        gsap.delayedCall(fastSpinDelay, () =>
                            playSound({ name: getRandomElement(m4UpgradeSounds) })
                        );
                    } else {
                        playSound({ name: getRandomElement(m4UpgradeSounds) });
                    }
                },
            });

            const featureAnimationSymbol = getSymbolFromPool(
                ApocalypseSpecialSymbolNames.BLANK
            ) as ApocalypseBlankSymbol;

            fsContainer.addChild(featureAnimationSymbol);

            symbolAnimationTl.add(
                gsap.fromTo(
                    featureAnimationSymbol,
                    { alpha: 0 },
                    {
                        alpha: 1,
                        duration: 0.05,
                        onStart: () => {
                            globalPosition = targetReel.getGlobalPosition();
                            localPosition = fsContainer.toLocal(globalPosition);
                            featureAnimationSymbol.x = localPosition.x;
                            featureAnimationSymbol.y = localPosition.y;
                        },
                    }
                ),
                0.5
            );
            symbolAnimationTl.add(featureAnimationSymbol.getFeatureAnimation(4), `>-=0.05`);
            symbolAnimationTl.to(
                featureAnimationSymbol,
                {
                    alpha: 0,
                    duration: 0.05,
                    onComplete: () => {},
                },
                `>-=0.05`
            );

            const levelNumber: number = +targetSymbol!.name.replace(/^\D+/g, ``);
            const newLevelNumber: number = levelNumber - 1;

            let newWinValue; //targetSymbol.valueObj.value;
            const oldValue = targetSymbol.valueObj.value;

            let highlightValue = targetSymbol.valueObj.value * 2;
            let highlightSymbol = targetSymbol;

            if (newLevelNumber < 1) {
                symbolAnimationTl.add(
                    targetSymbol.getUpdateMultiplierAnimation(targetSymbol.valueObj.value * 2),
                    `>-0.2`
                );

                newWinValue = oldValue;
                targetSymbol.valueObj.value *= 2;
            } else {
                const newSymbol = getSymbolFromPool(`L${newLevelNumber}`) as ApocalypseSymbol;
                newWinValue = newSymbol.valueObj.value * coordinates.symbolSize;

                newSymbol.getUpdateMultiplierAnimation(newWinValue, true);
                newSymbol.valueObj.value = newWinValue;

                newWinValue -= oldValue;

                symbolAnimationTl.add(
                    (gameReels[targetReelIndex] as ApocalypseFsReel).getUpgradeSymbolAnimation(
                        newSymbol
                    ),
                    `>-=0.5`
                );

                highlightValue = newWinValue * 2;
                highlightSymbol = newSymbol;
            }

            if (highlightValue >= 100) {
                localPosition = fsContainer.toLocal(targetSymbol.getGlobalPosition());
                addValueHighlightAnimation(
                    fsContainer,
                    targetSymbol,
                    highlightSymbol,
                    localPosition,
                    highlightValue,
                    symbolAnimationTl
                );
            }

            targetTl.add(symbolAnimationTl);

            symbolAnimationTimings.push(symbolAnimationTl.totalDuration());

            totalWinValue += newWinValue;

            const defaultMultiplierAnimation =
                fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                    targetSymbol.getGlobalPosition(),
                    newWinValue,
                    totalWinValue
                );

            multiplierAnimationTimings.push(defaultMultiplierAnimation.totalDuration());

            targetTl.add(defaultMultiplierAnimation);

            const getTlPosition = (): number => {
                if (coordIndex > 0) {
                    return (
                        symbolAnimationTimings[coordIndex - 1] +
                        multiplierAnimationTimings[coordIndex - 1] -
                        symbolAnimationTimings[coordIndex]
                    );
                }

                return 0;
            };

            currentTlPosition += getTlPosition();

            mainTargetTl.add(targetTl, currentTlPosition);
        });

        m4FeatureTimeline.add(mainTargetTl, 0.24);

        if (locker !== undefined) {
            m4FeatureTimeline.add(locker.getSpineTween(`idle_blinking`, true), 0);
        }

        const tlDuration = m4FeatureTimeline.totalDuration();
        const hideCloneLabel = `hideClone`;

        m4FeatureTimeline.addLabel(hideCloneLabel, tlDuration + 0.1);

        m4FeatureTimeline.call(
            () => {
                gsap.to(m4SymbolClone, {
                    alpha: 0,
                    duration: 0.2,
                    onComplete: () => {
                        returnSymbolToPool(m4SymbolClone);
                    },
                });
            },
            undefined,
            hideCloneLabel
        );

        m4FeatureTimeline.call(
            () => {
                m4SymbolClone.getMainSpine().getSpineTween(`feature_end`, false);
            },
            undefined,
            `${hideCloneLabel}-=0.7`
        );

        addSkip(m4FeatureTimeline, data.flowStateController!);

        return m4FeatureTimeline;
    }

    function getM3FeatureAnimation(
        feature: ApocalypseGameFeatureObj,
        onComplete: Function
    ): gsap.core.Timeline {
        const m3ReelIndex = getFlatIndex(feature.symbolCoord.reel, feature.symbolCoord.row);
        const m3Symbol = (
            gameReels[m3ReelIndex] as ApocalypseReel
        ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;
        const m3SymbolClone = getSymbolFromPool(m3Symbol.name) as ApocalypseSymbol;
        const locker = fsContainer.getLockerByReelId(m3ReelIndex);

        let globalPosition = m3Symbol.getGlobalPosition();
        let localPosition = fsContainer.toLocal(globalPosition);
        m3SymbolClone.x = localPosition.x;
        m3SymbolClone.y = localPosition.y;
        m3SymbolClone.alpha = 0;

        const featureAnimationSymbol = getSymbolFromPool(
            ApocalypseSpecialSymbolNames.BLANK
        ) as ApocalypseBlankSymbol;

        const m3FeatureTimeline = gsap.timeline({
            smoothChildTiming: true,
            onStart: () => {
                fsContainer.addChild(m3SymbolClone);

                playSound({ name: getRandomElement(m3FeatureSounds) });

                if (locker !== undefined) {
                    fsContainer.addChild(locker);
                }
            },
            onComplete: () => {
                onComplete();

                locker?.reset();

                returnSymbolToPool(featureAnimationSymbol);
            },
        });

        m3FeatureTimeline.add(
            gsap.to(m3SymbolClone, {
                alpha: 1,
                duration: 0.1,
            })
        );
        m3FeatureTimeline.add(m3SymbolClone.getHighSymbolFeatureAnimation());

        m3FeatureTimeline.call(
            () => {
                gsap.to(m3SymbolClone, {
                    alpha: 0,
                    duration: 0.2,
                    onComplete: () => {
                        returnSymbolToPool(m3SymbolClone);
                    },
                });
            },
            undefined,
            `>-=0.1`
        );

        feature.targetCoord.forEach((coordinates) => {
            const targetReelIndex = getFlatIndex(coordinates.reel, coordinates.row);
            const targetReel = gameReels[targetReelIndex] as ApocalypseReel;
            const targetSymbol = (
                gameReels[targetReelIndex] as ApocalypseReel
            ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;

            globalPosition = targetReel.getGlobalPosition();
            localPosition = fsContainer.toLocal(globalPosition);

            m3FeatureTimeline.add(
                gsap.fromTo(
                    featureAnimationSymbol,
                    { alpha: 0 },
                    {
                        alpha: 1,
                        duration: 0.05,
                        onStart: () => {
                            featureAnimationSymbol.x = localPosition.x;
                            featureAnimationSymbol.y = localPosition.y + 20;
                            fsContainer.addChild(featureAnimationSymbol);
                        },
                    }
                ),
                0.9
            );

            m3FeatureTimeline.add(featureAnimationSymbol.getFeatureAnimation(3), 0.91);
            m3FeatureTimeline.to(
                featureAnimationSymbol,
                {
                    alpha: 0,
                    duration: 0.32,
                },
                `>-=0.32`
            );

            const finalTargetValue = targetSymbol.valueObj.value * 2;
            const multiplierAnimation = targetSymbol.getM3MultiplierAnimation(finalTargetValue);

            m3FeatureTimeline.add(multiplierAnimation, `>-=${multiplierAnimation.totalDuration()}`);

            totalWinValue += targetSymbol.valueObj.value;

            if (finalTargetValue >= 100) {
                addValueHighlightAnimation(
                    fsContainer,
                    targetSymbol,
                    targetSymbol,
                    localPosition,
                    finalTargetValue,
                    m3FeatureTimeline
                );

                m3FeatureTimeline.add(
                    fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                        targetSymbol.getGlobalPosition(),
                        targetSymbol.valueObj.value,
                        totalWinValue
                    ),
                    `>`
                );
            } else {
                m3FeatureTimeline.add(
                    fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                        targetSymbol.getGlobalPosition(),
                        targetSymbol.valueObj.value,
                        totalWinValue
                    ),
                    `<+=0.4`
                );
            }
        });

        if (locker !== undefined) {
            m3FeatureTimeline.add(locker.getSpineTween(`idle_blinking`, true), 0);
        }

        addSkip(m3FeatureTimeline, data.flowStateController!);

        return m3FeatureTimeline;
    }

    // ! maybe getM2FeatureAnimation and getM1FeatureAnimation can use the same method
    function getM2FeatureAnimation(
        feature: ApocalypseGameFeatureObj,
        onComplete: Function
    ): gsap.core.Timeline {
        const m2ReelIndex = getFlatIndex(feature.symbolCoord.reel, feature.symbolCoord.row);
        const m2Symbol = (
            gameReels[m2ReelIndex] as ApocalypseReel
        ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;
        const m2SymbolClone = getSymbolFromPool(m2Symbol.name) as ApocalypseSymbol;

        const locker = fsContainer.getLockerByReelId(m2ReelIndex);

        let globalPosition = m2Symbol.getGlobalPosition();
        let localPosition = fsContainer.toLocal(globalPosition);
        m2SymbolClone.x = localPosition.x;
        m2SymbolClone.y = localPosition.y;
        m2SymbolClone.alpha = 0;
        fsContainer.addChild(m2SymbolClone);

        const featureAnimationSymbol = getSymbolFromPool(
            ApocalypseSpecialSymbolNames.BLANK
        ) as ApocalypseBlankSymbol;

        const m2FeatureTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                fsContainer.addChild(fsContainer.fsMultiplierContainer);

                if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    gsap.delayedCall(fastSpinDelay, () =>
                        playSound({ name: getRandomElement(m2FeatureSounds) })
                    );
                } else {
                    playSound({ name: getRandomElement(m2FeatureSounds) });
                }
                // playSound({ name: getRandomElement(m2FeatureSounds) });

                if (locker !== undefined) {
                    fsContainer.addChild(locker);
                }
            },
            onComplete: () => {
                onComplete();

                locker?.reset();

                returnSymbolToPool(featureAnimationSymbol);
            },
        });

        m2FeatureTimeline.add(
            gsap.to(m2SymbolClone, {
                alpha: 1,
                duration: 0.1,
            })
        );
        m2FeatureTimeline.add(m2SymbolClone.getHighSymbolFeatureAnimation());

        const collectTl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
        });

        feature.targetCoord.forEach((coordinates) => {
            const targetReelIndex = getFlatIndex(coordinates.reel, coordinates.row);
            const targetReel = gameReels[targetReelIndex] as ApocalypseReel;
            const targetSymbol = (
                gameReels[targetReelIndex] as ApocalypseReel
            ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;

            globalPosition = targetReel.getGlobalPosition();
            localPosition = fsContainer.toLocal(globalPosition);

            collectTl.add(
                gsap.fromTo(
                    featureAnimationSymbol,
                    { alpha: 0 },
                    {
                        alpha: 1,
                        duration: 0.05,
                        onStart: () => {
                            featureAnimationSymbol.x =
                                localPosition.x + symbolConfig.symbolWidth / 2;
                            featureAnimationSymbol.y =
                                localPosition.y + symbolConfig.symbolHeight / 2;
                            featureAnimationSymbol.pivot.set(
                                featureAnimationSymbol.width / 2,
                                symbolConfig.symbolHeight / 2
                            );

                            const rotation =
                                Math.atan2(
                                    m2SymbolClone.y -
                                        featureAnimationSymbol.y +
                                        symbolConfig.symbolHeight / 2,
                                    m2SymbolClone.x -
                                        featureAnimationSymbol.x +
                                        symbolConfig.symbolWidth / 2
                                ) + 1.5;
                            // * (180 / Math.PI);
                            featureAnimationSymbol.rotation = rotation;
                            fsContainer.addChild(featureAnimationSymbol);
                        },
                    }
                ),
                0
            );

            collectTl.add(featureAnimationSymbol.getFeatureAnimation(2), 0);

            collectTl.to(
                featureAnimationSymbol,
                {
                    alpha: 0,
                    duration: 0.1,
                },
                `>-=0.1`
            );

            const valueClone = cloneSymbolValue(
                targetSymbol.getGlobalPosition(),
                targetSymbol.valueObj.value,
                fsContainer
            );
            valueClone.alpha = 0;

            collectTl.add(
                gsap.to(valueClone, {
                    alpha: 1,
                    duration: 0.5,
                    x: m2SymbolClone.x + symbolConfig.symbolWidth / 2,
                    y: m2SymbolClone.y + symbolConfig.symbolHeight / 2 + 21.5,
                    onStart: () => {
                        fsContainer.addChild(valueClone);
                    },
                }),
                0.1
            );

            totalWinValue += targetSymbol.valueObj.value;

            collectTl.add(
                fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                    m2SymbolClone.getGlobalPosition(),
                    targetSymbol.valueObj.value,
                    totalWinValue
                ),
                `>+0.3`
            );

            collectTl.add(
                gsap.to(valueClone, {
                    alpha: 0,
                    duration: 0.4,
                    onComplete: () => {
                        fsContainer.removeChild(valueClone);
                        valueClone.destroy();
                    },
                }),
                `<`
            );
        });

        m2FeatureTimeline.add(collectTl, 0.6);

        m2FeatureTimeline.call(
            () => {
                gsap.to(m2SymbolClone, {
                    alpha: 0,
                    duration: 0.2,
                    onComplete: () => {
                        returnSymbolToPool(m2SymbolClone);
                    },
                });
            },
            undefined,
            `>-=0.2`
        );

        if (locker !== undefined) {
            m2FeatureTimeline.add(locker.getSpineTween(`idle_blinking`, true), 0);
        }

        addSkip(m2FeatureTimeline, data.flowStateController!);

        return m2FeatureTimeline;
    }

    function getM1FeatureAnimation(
        feature: ApocalypseGameFeatureObj,
        onComplete: Function
    ): gsap.core.Timeline {
        const m1ReelFlatIndex = getFlatIndex(feature.symbolCoord.reel, feature.symbolCoord.row);
        const m1Symbol = (
            gameReels[m1ReelFlatIndex] as ApocalypseReel
        ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;
        const m1SymbolClone = getSymbolFromPool(m1Symbol.name) as ApocalypseSymbol;

        const locker = fsContainer.getLockerByReelId(m1ReelFlatIndex);

        let globalPosition = m1Symbol.getGlobalPosition();
        let localPosition = fsContainer.toLocal(globalPosition);
        m1SymbolClone.x = localPosition.x;
        m1SymbolClone.y = localPosition.y;
        m1SymbolClone.alpha = 0;

        const crowsContainer = new Container();
        crowsContainer.name = `crowsContainer`;
        crowsContainer.alpha = 0;
        fsContainer.addChild(crowsContainer);

        const m1FeatureTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                fsContainer.addChild(m1SymbolClone);

                // playSound({ name: getRandomElement(m1FeatureSounds) });
                if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    gsap.delayedCall(fastSpinDelay, () =>
                        playSound({ name: getRandomElement(m1FeatureSounds) })
                    );
                } else {
                    playSound({ name: getRandomElement(m1FeatureSounds) });
                }

                if (locker !== undefined) {
                    fsContainer.addChild(locker);
                }
            },
            onComplete: () => {
                onComplete();

                crowsContainer.destroy();

                locker?.reset();
            },
        });

        m1FeatureTimeline.add(
            gsap.to(m1SymbolClone, {
                alpha: 1,
                duration: 0.1,
            })
        );

        let leftEyeOccupied: boolean = false;
        let rightEyeOccupied: boolean = false;
        let eyeCounter: number = 0;
        let m1ReelIndex = feature.symbolCoord.row;

        let birdsMoveMinDuration = 100000000;
        let birdsMoveMaxDuration = 0;

        feature.targetCoord.forEach((coordinates, targetIndex) => {
            const targetReelFlatIndex = getFlatIndex(coordinates.reel, coordinates.row);
            const targetReelIndex = coordinates.row;

            const targetSymbol = (
                gameReels[targetReelFlatIndex] as ApocalypseReel
            ).getAllVisibleStopSymbols()[0] as ApocalypseSymbol;

            const crowSpine = new AbstractSpine(ApocalypseSpineNames.CROW);
            crowSpine.scale.set(0.2);
            crowSpine.name = `crow_` + targetIndex.toString();

            crowsContainer.addChild(crowSpine);

            let targetGlobalPosition = targetSymbol.getGlobalPosition();
            let targetLocalPosition = fsContainer.toLocal(targetGlobalPosition);
            const crowFinalPosX = targetLocalPosition.x + symbolConfig.symbolWidth / 2;
            const crowFinalPosY = targetLocalPosition.y + symbolConfig.symbolHeight / 2;

            crowSpine.x = crowFinalPosX;
            crowSpine.y = crowFinalPosY;

            crowSpine.rotation = Math.PI / 2;
            if (targetReelIndex > m1ReelIndex) {
                rightEyeOccupied = true;
                crowSpine.scale.x *= -1;
                crowSpine.rotation *= -1;
            } else if (targetReelIndex < m1ReelIndex) {
                leftEyeOccupied = true;
            } else {
                gsap.set(crowSpine, {
                    pixi: {
                        colorize: `orange`,
                        colorizeAmount: 1,
                        brightness: 2,
                    },
                });

                if (!leftEyeOccupied) {
                    leftEyeOccupied = true;
                } else if (!rightEyeOccupied) {
                    rightEyeOccupied = true;
                    eyeCounter = 1;
                    crowSpine.scale.x *= -1;
                    crowSpine.rotation *= -1;
                } else {
                    if (eyeCounter === 0) {
                        leftEyeOccupied = true;
                    } else {
                        rightEyeOccupied = true;
                        crowSpine.scale.x *= -1;
                        crowSpine.rotation *= -1;
                    }
                }

                eyeCounter++;
                if (eyeCounter > 1) {
                    eyeCounter = 0;
                }
            }

            const rotation = Math.atan2(
                m1SymbolClone.y - crowFinalPosY,
                m1SymbolClone.x - crowFinalPosX
            );

            const distance = Math.sqrt(
                Math.pow(m1SymbolClone.x - crowFinalPosX, 2) +
                    Math.pow(m1SymbolClone.y - crowFinalPosY, 2)
            );
            let finalRotation = rotation + 2 * Math.PI;
            let finalScale = 0.45;
            crowSpine.x = m1SymbolClone.x + symbolConfig.symbolWidth / 2 - 34;
            crowSpine.y = m1SymbolClone.y + symbolConfig.symbolHeight / 2 - 25;
            crowSpine.rotation = 0;

            if (crowSpine.scale.x < 0) {
                finalRotation = rotation + Math.PI + Math.PI / 10;
                finalScale = -0.45;
                crowSpine.x += 68;
            }

            crowSpine.getSpineTween(`idle`, true);

            const moveBirdToFinalPositionAnimation = gsap.to(crowSpine, {
                x: crowFinalPosX,
                y: crowFinalPosY,
                pixi: {
                    scaleX: finalScale,
                    scaleY: 0.45,
                    rotation: `${(finalRotation * 180.0) / Math.PI}_short`,
                },
                duration: 0.1 + distance / (600 - targetReelFlatIndex * 0.02),
                ease: `power1.in`,
            });

            m1FeatureTimeline.add(moveBirdToFinalPositionAnimation, 1.05);

            const smashAnimation = crowSpine.getSpineTween(`smash`);
            m1FeatureTimeline.add(smashAnimation, `>`);

            totalWinValue += targetSymbol.valueObj.value;

            const defaultMultiplierAnimation =
                fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                    targetSymbol.getGlobalPosition(),
                    targetSymbol.valueObj.value,
                    totalWinValue,
                    true
                );
            m1FeatureTimeline.add(defaultMultiplierAnimation, `>`);

            if (
                moveBirdToFinalPositionAnimation.duration() +
                    smashAnimation.duration() +
                    defaultMultiplierAnimation.duration() >
                birdsMoveMaxDuration
            ) {
                birdsMoveMaxDuration =
                    moveBirdToFinalPositionAnimation.duration() +
                    smashAnimation.duration() +
                    defaultMultiplierAnimation.duration();
            }

            if (
                moveBirdToFinalPositionAnimation.duration() +
                    smashAnimation.duration() +
                    defaultMultiplierAnimation.duration() <
                birdsMoveMinDuration
            ) {
                birdsMoveMinDuration =
                    moveBirdToFinalPositionAnimation.duration() +
                    smashAnimation.duration() +
                    defaultMultiplierAnimation.duration();
            }
        });

        const winLevel = getWinLevel(totalWinValue);

        if (totalWinValue > maxWinLevel.min) {
            totalWinValue = maxWinLevel.min;
        }

        let duration = Math.max(
            Math.min(winLevel.animationDuration / 6, 1),
            birdsMoveMaxDuration - birdsMoveMinDuration + 0.2
        );

        m1FeatureTimeline.add(
            fsContainer.fsMultiplierContainer.counter.getUpdateCounterTextAnimation(
                totalWinValue,
                false,
                true,
                duration
            ),
            1.06 + birdsMoveMinDuration
        );

        let featureNumber = 2;

        if (!leftEyeOccupied && rightEyeOccupied) {
            featureNumber = 3;
        } else if (leftEyeOccupied && !rightEyeOccupied) {
            featureNumber = 1;
        }

        m1FeatureTimeline.add(m1SymbolClone.getHighSymbolFeatureAnimation(featureNumber), 0.1);

        m1FeatureTimeline.call(
            () => {
                gsap.to(m1SymbolClone, {
                    alpha: 0,
                    duration: 0.2,
                    onComplete: () => {
                        returnSymbolToPool(m1SymbolClone);
                    },
                });
            },
            undefined,
            `>-=0.2`
        );

        m1FeatureTimeline.add(
            gsap.to(crowsContainer, {
                alpha: 1,
                duration: 0.55,
                onStart: () => {
                    fsContainer.addChild(crowsContainer);
                },
            }),
            0.85
        );

        if (locker !== undefined) {
            m1FeatureTimeline.add(locker.getSpineTween(`idle_blinking`, true), 0);
        }

        addSkip(m1FeatureTimeline, data.flowStateController!);

        return m1FeatureTimeline;
    }
};

const addValueHighlightAnimation = (
    fsContainer: Container,
    symbol: ApocalypseSymbol,
    finalSymbol: ApocalypseSymbol,
    symbolPosition: Point,
    value: number,
    tl: gsap.core.Timeline
): void => {
    let tintColor = 0x35e21e;
    let shakeValue = 2;

    if (value >= 500) {
        tintColor = 0xf4ed15;
        shakeValue = 4;
    }
    if (value >= 1000) {
        tintColor = 0xfc603f;
        shakeValue = 6;
    }

    const counterClone = getBitmapText();
    counterClone.name = `counterClone`;
    counterClone.x = symbolPosition.x + 53.5;
    counterClone.y = symbolPosition.y + 73.5;
    counterClone.alpha = 0;

    const maxValue = value > 20000 ? 20000 : value;
    counterClone.updateValue(`x${maxValue}`);
    fitDisplayObjectToWidth(counterClone, 73);

    fsContainer.addChild(counterClone);

    const showCounterClone = gsap.to(counterClone, {
        alpha: 1,
        pixi: {
            colorize: tintColor,
            colorizeAmount: 1,
        },
        duration: 0.2,
        onStart: () => {
            shake(finalSymbol, shakeValue);
        },
    });

    tl.add(showCounterClone, `>`);

    const hideTargetCounter = gsap.to(symbol.counter, {
        alpha: 0,
        duration: 0.3,
    });

    tl.add(hideTargetCounter, `<`);

    const scaleCounterClone = gsap.to(counterClone, {
        duration: 0.6,
        alpha: 0,
        pixi: {
            scale: `+=0.2`,
        },
        onComplete: () => {
            returnBitmapText(counterClone);
        },
    });

    tl.add(scaleCounterClone, `>`);

    const showTargetCounter = gsap.to(symbol.counter, {
        alpha: 1,
        duration: 0.4,
    });

    tl.add(showTargetCounter, `>-0.4`);
};
